import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MousePointerClick, TrendingUp, DollarSign } from "lucide-react";
import SEO from "@/components/SEO";

export default function Affiliates() {
  const [form, setForm] = useState({ name: "", email: "", payout_email: "", referral_code: "" });
  const [dashboardCode, setDashboardCode] = useState("");
  const [aff, setAff] = useState(null);
  const [busy, setBusy] = useState(false);

  const apply = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/affiliates/apply", form);
      toast.success(`Application received. Your code: ${r.data.referral_code}`);
      setForm({ name: "", email: "", payout_email: "", referral_code: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    setBusy(false);
  };

  const lookup = async (e) => {
    e.preventDefault();
    try {
      const r = await api.get(`/affiliates/${dashboardCode.toUpperCase()}`);
      setAff(r.data);
    } catch { toast.error("Referral code not found"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="affiliates-page">
      <SEO
        title="Affiliate Program — Earn 20% Commission"
        description="Join the Aperture Enterprises affiliate program. Share our creative real estate playbooks and earn 20% commission on every sale."
        canonical="https://apertureent.com/affiliates"
      />
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <div className="eyebrow mb-3">Aperture Referral Program</div>
          <h1 className="aperture-h1 text-4xl md:text-5xl leading-tight">Earn 20%<br />for every reader you send.</h1>
          <p className="mt-6 text-slate-700 leading-relaxed">
            Share our playbooks with your list, your investors, or your community. Get a unique referral link, track every click and sale, and receive commission on each purchase.
          </p>

          <div className="mt-12 space-y-6">
            {[
              { i: MousePointerClick, t: "Get your link", d: "Instant referral link on approval." },
              { i: TrendingUp, t: "Track everything", d: "Live dashboard: clicks, sales, commission." },
              { i: DollarSign, t: "Get paid", d: "Monthly payouts via your preferred method." },
            ].map((s, i) => (
              <div key={i} className="flex gap-5 items-start">
                <div className="w-10 h-10 border border-gold rounded-full flex items-center justify-center flex-shrink-0"><s.i className="w-4 h-4 text-gold" strokeWidth={1.5}/></div>
                <div>
                  <div className="font-serif text-lg text-navy">{s.t}</div>
                  <div className="text-sm text-slate-600">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-10">
          <form onSubmit={apply} className="bg-white p-8 shadow-xl rounded-sm space-y-4" data-testid="affiliate-apply-form">
            <div className="eyebrow">Apply</div>
            <input required placeholder="Full name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="aff-name" className="w-full px-4 py-3 border rounded-sm focus:outline-none focus:border-gold text-sm" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="aff-email" className="w-full px-4 py-3 border rounded-sm focus:outline-none focus:border-gold text-sm" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            <input placeholder="Payout email (PayPal / Wise / etc.)" value={form.payout_email} onChange={(e)=>setForm({...form, payout_email:e.target.value})} data-testid="aff-payout" className="w-full px-4 py-3 border rounded-sm focus:outline-none focus:border-gold text-sm" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            <input placeholder="Preferred referral code (optional)" value={form.referral_code} onChange={(e)=>setForm({...form, referral_code:e.target.value})} data-testid="aff-code" className="w-full px-4 py-3 border rounded-sm focus:outline-none focus:border-gold text-sm" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            <button disabled={busy} data-testid="aff-submit" className="btn-primary w-full py-3 text-xs uppercase tracking-widest rounded-sm">{busy ? "Submitting…" : "Apply Now"}</button>
          </form>

          <form onSubmit={lookup} className="bg-white p-8 shadow-xl rounded-sm space-y-4" data-testid="affiliate-lookup-form">
            <div className="eyebrow">Existing affiliate? Check your dashboard</div>
            <input placeholder="Your referral code" value={dashboardCode} onChange={(e)=>setDashboardCode(e.target.value)} data-testid="aff-lookup-code" className="w-full px-4 py-3 border rounded-sm focus:outline-none focus:border-gold text-sm" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            <button data-testid="aff-lookup-submit" className="border border-navy text-navy w-full py-3 text-xs uppercase tracking-widest hover:bg-navy hover:text-white transition-colors duration-300 rounded-sm">View Dashboard</button>
            {aff && (
              <div className="mt-6 border-t pt-6 space-y-2 text-sm" data-testid="aff-dashboard">
                <div><span className="text-navy/60">Status:</span> <span className={aff.status === "approved" ? "text-gold" : "text-navy"}>{aff.status}</span></div>
                <div><span className="text-navy/60">Commission:</span> {aff.commission_pct}%</div>
                <div><span className="text-navy/60">Clicks:</span> {aff.total_clicks}</div>
                <div><span className="text-navy/60">Sales:</span> ${aff.total_sales.toFixed(2)}</div>
                <div><span className="text-navy/60">Earned:</span> <span className="text-gold font-medium">${aff.total_commission.toFixed(2)}</span></div>
                {aff.status === "approved" && (
                  <div className="pt-3 mt-3 border-t">
                    <div className="text-xs uppercase tracking-widest text-navy/60 mb-1">Referral URL</div>
                    <div className="text-xs text-navy break-all">{window.location.origin}/?ref={aff.referral_code}</div>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
